import React, { Fragment, useMemo, useState, useEffect } from 'react';
import { storage } from '@uhg-abyss/web/tools/storage';
import { config } from '@uhg-abyss/web/tools/config';
import { styled } from '@uhg-abyss/web/tools/styled';
import { Button } from '@uhg-abyss/web/ui/Button';
import { useUserInfo } from '@src/hooks/useUserInfo';
import { LoadingOverlay } from '@uhg-abyss/web/ui/LoadingOverlay';
import { useDSUserInfo } from '@src/hooks/useDsUserInfo';
import { Tabs } from '@uhg-abyss/web/ui/Tabs';
import { useRouter } from '@uhg-abyss/web/hooks/useRouter';
import { useDataTable } from '@uhg-abyss/web/hooks/useDataTable';
import { Alert } from '@uhg-abyss/web/ui/Alert';
import { DataTable } from '@uhg-abyss/web/ui/DataTable';
import { event } from '@uhg-abyss/web/tools/event';
import { LagoonContext } from '@src/AppContext';
import { Breadcrumbs } from '@uhg-abyss/web/ui/Breadcrumbs';
import { AllCommunicationPageDesc } from './AllCommunicationPageDescription';
import { useGetCommunicationAPI } from '@src/hooks/useGetCommunicationAPI';
import { ViewEmailModal } from '../CreateCommunication/ViewEmailModal';
import { useGetSentCommunicationApi } from '@src/hooks/useGetSentCommunicationApi';
import { constants } from '@src/common/Utils/Constants';
import { mapBreadcrumbsData } from '@src/common/Utils/utils';
import { useSelector } from 'react-redux';

const PageHeading = styled('h1', {
  color: '#002677',
  fontSize: '41.04px',
  fontWeight: '600',
  lineHeight: '52px',
  marginBottom: '32px',
  '@screen < $sm': {
    fontSize: '25px',
  },
});

const BreadcrumbStyles = styled('div', {
  marginTop: '20px',
});

const AlertStyle = styled('div', {
  marginBottom: '35px',
});

export const AllCommunications = () => {
  const [userDSInfo, getDSUserInfo] = useDSUserInfo();
  const userDSData = userDSInfo?.data;
  const callDsUserInfo = !userDSData?.uuid && !userDSData?.status;
  const [dsUserInfoCalled, setDsUserInfoCalled] = useState(false);
  const [breadcrumbItems, setBreadcrumbItems] = useState([]);
  const [userInfo, getUserInfo] = useUserInfo();
  const userInfoData = userInfo.data;
  const [isLoading, setisLoading] = useState(false);
  const [communicationAPI, getCommunicationAPI] = useGetCommunicationAPI();
  const [commId, setCommId] = useState(null);
  const { recentMessages = [], archivedMessages = [] } =
    communicationAPI.data || {};
  const [sentCommunication, getSentCommunication] =
    useGetSentCommunicationApi();
  const [displayError, setDisplayError] = useState(false);
  const lagoonContext = React.useContext(LagoonContext);
  const errorMessages = lagoonContext?.getTable('error-messages').getRow();
  const [serverErrorMessage, setServerErrorMessage] = useState({
    header: '',
    message: '',
  });
  const [showViewEmailModal, setShowViewEmailCommModal] = useState(false);

  const getErrorMessage = (messageKey) => {
    const messageExist = errorMessages.find((message) => {
      return message?.active && message?.key === messageKey;
    });
    if (messageExist) {
      setDisplayError(true);
      const { header = '', message = '' } = messageExist || {};
      setServerErrorMessage({ header, message });
    }
  };

  const { navigate } = useRouter();
  const isUserActive = userDSData?.status && userDSData?.status === 'ACTIVE';
  const modalName = constants.UHC_NOTIFY_MODAL_NAME;

  const breadcrumbsData = useSelector((state) => {
    return state.BREADCRUMBS.data;
  });

  useEffect(() => {
    if (!isUserActive) navigate('/welcome');
    storage.local.set('IS_SIGN_IN_CLICKED', false);
    if (!document.getElementById('auth-script')) {
      const script = document.createElement('script');
      script.src = config('UHC_AUTH_SCRIPT');
      script.async = false;
      script.id = 'auth-script';
      document.head.appendChild(script);
    }

    if (!userInfo.data.firstName) {
      getUserInfo();
    }
    if (userInfo?.data?.firstName && callDsUserInfo) {
      setDsUserInfoCalled(true);
      getDSUserInfo({ userInfoData });
    }
  }, []);

  useEffect(() => {
    // Use the breadcrumbs data from Redux
    if (breadcrumbsData && Array.isArray(breadcrumbsData)) {
      const items = mapBreadcrumbsData(breadcrumbsData);
      setBreadcrumbItems(items);
    }
  }, [breadcrumbsData]); // Only re-run when breadcrumbs data or URL changes

  useEffect(() => {
    if (userInfoData?.firstName && callDsUserInfo && !dsUserInfoCalled) {
      setDsUserInfoCalled(true);
      getDSUserInfo({ userInfoData });
    }
  }, [userInfo]);

  useEffect(() => {
    //  if ((!userDSInfo?.loading && userDSData && userDSData?.uuid)) {
    getCommunicationAPI({ userInfo, allMessages: true });
    // }
  }, []);

  useEffect(() => {
    setDisplayError(false);
    if (!communicationAPI?.loading) {
      if (communicationAPI.error) {
        getErrorMessage('GET_COMMUNICATIONS_FAILED');
      } else {
        setDisplayError(false);
      }
      setisLoading(false);
    } else {
      setisLoading(true);
    }
  }, [communicationAPI]);

  useEffect(() => {
    if (
      !sentCommunication.loading &&
      commId !== null &&
      Object.keys(sentCommunication?.data || {})?.length
    ) {
      setShowViewEmailCommModal(true);
    }
    if (sentCommunication?.error) {
      setShowViewEmailCommModal(false);
      getErrorMessage('GET_VIEW_EMAIL_FAILED');
    }
  }, [sentCommunication]);

  const onViewEmailClick = (id, interName, interContext) => {
    setCommId(id);
    getSentCommunication({ userInfo, id });
    setDisplayError(false);
    const interactionName = interName;
    const interactionValue = 'View Communication';
    const interactionContext = interContext;
    event('INTERACTION_EVENT', {
      interactionName,
      interactionValue,
      interactionContext,
    });
    event('MODAL_VIEW', {
      event,
      modalName,
    });
  };

  const viewComm = (id, interName, interContext) => {
    return (
      <Button
        id="viewCommButton"
        variant="outline"
        size="$md"
        onClick={() => {
          return onViewEmailClick(id, interName, interContext);
        }}
        key={id}
        fontSize="15px"
        ariaLoadingLabel="Loading content"
        isLoading={commId === id && sentCommunication?.loading}
      >
        View Communication
      </Button>
    );
  };

  const createRecentCommData =
    recentMessages?.map((item, ind) => {
      return {
        key: ind,
        recentDate: item.date,
        recentSubject: item.subject,
        id: item.id,
        actions: viewComm(item.id, item.subject, 'Recent Communications'),
      };
    }) || [];
  const createArchiveCommData =
    archivedMessages?.map((item, ind) => {
      return {
        key: ind,
        archiveDate: item.date,
        archiveSubject: item.subject,
        actions: viewComm(item.id, item.subject, 'Archived Communications'),
      };
    }) || [];

  const recentColumns = [
    {
      Header: 'Subject',
      accessor: 'recentSubject',
    },
    {
      Header: 'Release Date',
      accessor: 'recentDate',
    },
    {
      Header: 'Actions',
      accessor: 'actions',
    },
  ];

  const archiveColumns = [
    {
      Header: 'Subject',
      accessor: 'archiveSubject',
    },
    {
      Header: 'Release Date',
      accessor: 'archiveDate',
    },
    {
      Header: 'Actions',
      accessor: 'actions',
    },
  ];

  const recentData = useMemo(() => {
    return [...createRecentCommData];
  }, [communicationAPI]);
  const recentDataTableProps = useDataTable({
    initialData: recentData,
    initialColumns: recentColumns,
    showPagination: true,
    showTopPagination: false,
    noDataMessage: 'Your recent communication folder is empty',
  });

  const archiveData = useMemo(() => {
    return [...createArchiveCommData];
  }, [communicationAPI]);
  const archiveDataTableProps = useDataTable({
    initialData: archiveData,
    initialColumns: archiveColumns,
    showPagination: true,
    showTopPagination: false,
    noDataMessage: 'Your Archives folder is empty',
  });
  const onViewEmailModalClose = () => {
    event('MODAL_CLOSE', {
      event,
      modalName,
    });
    setShowViewEmailCommModal(false);
  };

  useEffect(() => {
    recentDataTableProps.setData(createRecentCommData, true);
    archiveDataTableProps.setData(createArchiveCommData, true);
  }, [communicationAPI, sentCommunication]);

  const handleTabChange = (tab) => {
    const interactionName = 'All Communications';
    const interactionValueList = [
      'Recent Communication',
      'Archived Communications',
    ];
    const interactionValue = interactionValueList[tab];
    const interactionContext = 'tab clicks';
    event('INTERACTION_EVENT', {
      interactionName,
      interactionValue,
      interactionContext,
    });
  };

  return (
    <Fragment>
      {showViewEmailModal && (
        <ViewEmailModal
          showModal={showViewEmailModal}
          modalOnClose={onViewEmailModalClose}
          emailTemplate={sentCommunication.data}
        />
      )}
      <BreadcrumbStyles>
        <Breadcrumbs
          size="11px"
          css={{
            fontWeight: 400,
            'abyss-breadcrumb-current': { fontWeight: 400 },
          }}
          items={breadcrumbItems}
        />
      </BreadcrumbStyles>
      <PageHeading id="welcome-user">
        Welcome {userInfo.data.firstName}
      </PageHeading>
      <AlertStyle>
        {displayError ? (
          <Alert title={serverErrorMessage?.header}>
            {serverErrorMessage?.message}
          </Alert>
        ) : null}
      </AlertStyle>

      <AllCommunicationPageDesc />

      <LoadingOverlay
        loadingTitle="Please Wait"
        loadingMessage="while we retrieve your data."
        ariaLoadingLabel="Example loading aria label"
        isLoading={isLoading}
        width="40%"
      >
        <Tabs grow={false} onTabChange={handleTabChange}>
          <Tabs.Tab
            label="Recent Communications"
            id="recentCommunicationsTab"
            style={{ marginTop: '35px', fontSize: '15px' }}
          >
            <DataTable
              hideTitleHeader
              tableState={recentDataTableProps}
              id="recentDataTable"
              css={{
                'abyss-table-cell': { fontSize: '13px', fontWeight: 400 },
              }}
            />
          </Tabs.Tab>
          <Tabs.Tab
            label="Archived Communications"
            id="archivedCommunicationsTab"
            style={{ marginTop: '35px', fontSize: '15px' }}
          >
            <DataTable
              hideTitleHeader
              tableState={archiveDataTableProps}
              id="archiveDataTable"
              css={{
                'abyss-table-cell': { fontSize: '13px', fontWeight: 400 },
              }}
            />
          </Tabs.Tab>
        </Tabs>
      </LoadingOverlay>
    </Fragment>
  );
};
